/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../config/axiosConfig';
import { API } from '../utils/apiRoutes';
import { useAuth } from './AuthContext';
import { useMenuContext } from './MenuContext';
import { canAccessSection } from '../config/sectionAccess';
import AssistantConversationDialogs from '../components/Dashboard/DashboardContents/Assistant/AssistantConversationDialogs';

// Shared state of the «دستیار هوشمند»: the sidebar branch (recent threads,
// new/all) and the chat page read the same list, drafts and in-flight sends,
// so switching sections or threads never duplicates requests or loses text.
// MainPage keys this provider by user+institute: logging out or switching
// identity remounts it, which drops every cached thread and silences any
// request still in flight (aliveRef).

const AssistantContext = createContext(null);

export const useAssistant = () => useContext(AssistantContext);

// The server runs a tool loop, so a reply can take a while.
export const ASSISTANT_TIMEOUT_MS = 90000;
export const RECENT_LIMIT = 5;
export const NEW_KEY = 'new';
const CONVERSATION_PARAM = /^[1-9]\d{0,9}$/;

const newClientId = () => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
    // RFC 4122 v4 fallback for older browsers.
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
};

export function sendErrorMessage(err, fallback = 'پاسخ‌گویی دستیار با خطا مواجه شد.') {
    const status = err?.response?.status;
    if (status === 429) return 'تعداد پرسش‌های شما زیاد است؛ کمی بعد دوباره تلاش کنید.';
    if (err?.code === 'ECONNABORTED') {
        return 'پاسخ در زمان معمول نرسید. پیش از ارسال دوباره، وضعیت پیام بررسی می‌شود.';
    }
    if (!err?.response) return 'ارتباط با سرور برقرار نشد.';
    return err.response.data?.detail || fallback;
}

export function AssistantProvider({ children }) {
    const { user } = useAuth();
    const { selectedMenuItem } = useMenuContext();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const enabled = canAccessSection(user, 'assistant');
    const onAssistant = selectedMenuItem === 'assistant';

    const aliveRef = useRef(true);
    useEffect(() => {
        aliveRef.current = true;
        return () => {
            aliveRef.current = false;
        };
    }, []);

    // --- URL-derived selection -------------------------------------------
    const rawConversation = onAssistant ? searchParams.get('conversation') : null;
    const selectedId =
        rawConversation && CONVERSATION_PARAM.test(rawConversation) ? Number(rawConversation) : null;
    const invalidParam = Boolean(rawConversation) && selectedId === null;
    const view = onAssistant && searchParams.get('view') === 'history' ? 'history' : 'chat';
    const [newChatNonce, setNewChatNonce] = useState(0);
    const locationRef = useRef({ selectedId, view, onAssistant, newChatNonce });
    locationRef.current = { selectedId, view, onAssistant, newChatNonce };

    const openConversation = useCallback(
        (id) => navigate(`/dashboard/assistant?conversation=${id}`),
        [navigate],
    );
    const openNew = useCallback(() => {
        setNewChatNonce((n) => n + 1);
        navigate('/dashboard/assistant');
    }, [navigate]);
    const openHistory = useCallback(() => navigate('/dashboard/assistant?view=history'), [navigate]);

    // --- status ------------------------------------------------------------
    const [status, setStatus] = useState({ data: null, error: null, httpStatus: null, loading: enabled });
    const loadStatus = useCallback(async () => {
        setStatus((prev) => ({ ...prev, loading: true, error: null }));
        try {
            const res = await api.get(API.assistantStatus);
            if (aliveRef.current) setStatus({ data: res.data, error: null, httpStatus: 200, loading: false });
        } catch (err) {
            if (aliveRef.current) {
                setStatus({
                    data: null,
                    error: sendErrorMessage(err, 'وضعیت دستیار دریافت نشد.'),
                    httpStatus: err?.response?.status ?? null,
                    loading: false,
                });
            }
        }
    }, []);
    useEffect(() => {
        if (enabled) loadStatus();
    }, [enabled, loadStatus]);

    // --- recent list (first page, newest activity first) -------------------
    const [recent, setRecent] = useState({ items: [], loading: enabled, error: null });
    const [listVersion, setListVersion] = useState(0);
    const recentTokenRef = useRef(0);
    const refreshRecent = useCallback(async () => {
        const token = ++recentTokenRef.current;
        try {
            const res = await api.get(API.assistantConversations);
            if (!aliveRef.current || token !== recentTokenRef.current) return;
            const items = res.data?.results || res.data || [];
            setRecent({ items, loading: false, error: null });
        } catch (err) {
            if (!aliveRef.current || token !== recentTokenRef.current) return;
            setRecent((prev) => ({ ...prev, loading: false, error: sendErrorMessage(err, 'فهرست گفتگوها دریافت نشد.') }));
        }
    }, []);
    useEffect(() => {
        if (enabled) refreshRecent();
    }, [enabled, refreshRecent]);
    const listChanged = useCallback(() => {
        setListVersion((v) => v + 1);
        refreshRecent();
    }, [refreshRecent]);

    // --- conversation details cache -----------------------------------------
    // id -> { loading, data, error, httpStatus }
    const [details, setDetails] = useState({});
    const detailTokens = useRef({});
    const loadDetail = useCallback(async (id, { silent = false } = {}) => {
        const token = (detailTokens.current[id] || 0) + 1;
        detailTokens.current[id] = token;
        if (!silent) {
            setDetails((prev) => ({ ...prev, [id]: { ...(prev[id] || {}), loading: true, error: null } }));
        }
        try {
            const res = await api.get(`${API.assistantConversations}${id}/`);
            if (!aliveRef.current || detailTokens.current[id] !== token) return null;
            setDetails((prev) => ({ ...prev, [id]: { loading: false, data: res.data, error: null, httpStatus: 200 } }));
            return res.data;
        } catch (err) {
            if (!aliveRef.current || detailTokens.current[id] !== token) return null;
            const httpStatus = err?.response?.status ?? null;
            setDetails((prev) => ({
                ...prev,
                [id]: {
                    loading: false,
                    // A missing/forbidden thread must not keep showing old data.
                    data: httpStatus === 404 || httpStatus === 403 ? null : prev[id]?.data || null,
                    error: sendErrorMessage(err, 'گفتگو دریافت نشد.'),
                    httpStatus,
                },
            }));
            return null;
        }
    }, []);

    const appendMessages = useCallback((id, newMessages) => {
        setDetails((prev) => {
            const entry = prev[id];
            if (!entry?.data) return prev;
            const existing = entry.data.messages || [];
            const ids = new Set(existing.map((m) => m.id));
            const merged = [...existing, ...newMessages.filter((m) => m && !ids.has(m.id))];
            return { ...prev, [id]: { ...entry, data: { ...entry.data, messages: merged } } };
        });
    }, []);

    // --- drafts (per thread; 'new' for an unsent thread) --------------------
    const [drafts, setDrafts] = useState({});
    const setDraft = useCallback((key, text) => setDrafts((prev) => ({ ...prev, [key]: text })), []);

    // --- pending sends --------------------------------------------------------
    // key ('new:<nonce>' or id) -> [{ clientId, content, state, error, conversationId }]
    const [pending, setPending] = useState({});
    const pendingRef = useRef(pending);
    pendingRef.current = pending;
    const patchPending = useCallback((key, clientId, patch) => {
        setPending((prev) => {
            const list = prev[key] || [];
            const next = patch === null
                ? list.filter((p) => p.clientId !== clientId)
                : list.map((p) => (p.clientId === clientId ? { ...p, ...patch } : p));
            return { ...prev, [key]: next };
        });
    }, []);
    const movePending = useCallback((fromKey, toKey, clientId, patch) => {
        setPending((prev) => {
            const item = (prev[fromKey] || []).find((p) => p.clientId === clientId);
            if (!item) return prev;
            return {
                ...prev,
                [fromKey]: (prev[fromKey] || []).filter((p) => p.clientId !== clientId),
                [toKey]: [...(prev[toKey] || []), { ...item, ...patch }],
            };
        });
    }, []);

    const hasReply = (conversation, clientId) => {
        const messages = conversation?.messages || [];
        const index = messages.findIndex((m) => m.role === 'user' && m.client_request_id === clientId);
        return index !== -1 && messages[index + 1]?.role === 'assistant';
    };

    const deliver = useCallback(async (key, item) => {
        const { clientId, content } = item;
        const conversationId = typeof key === 'number' ? key : null;
        patchPending(key, clientId, { state: 'sending', error: null });
        try {
            if (conversationId) {
                const res = await api.post(
                    `${API.assistantConversations}${conversationId}/messages/`,
                    { message: content, client_request_id: clientId },
                    { timeout: ASSISTANT_TIMEOUT_MS },
                );
                if (!aliveRef.current) return;
                appendMessages(conversationId, [res.data.user_message, res.data.assistant_message]);
                patchPending(key, clientId, null);
            } else {
                const res = await api.post(
                    API.assistantConversations,
                    { message: content, client_request_id: clientId },
                    { timeout: ASSISTANT_TIMEOUT_MS },
                );
                if (!aliveRef.current) return;
                const created = res.data;
                setDetails((prev) => ({ ...prev, [created.id]: { loading: false, data: created, error: null, httpStatus: 200 } }));
                patchPending(key, clientId, null);
                const loc = locationRef.current;
                // Only follow the new thread if the user is still looking at the
                // blank chat this message was sent from.
                if (loc.onAssistant && loc.view === 'chat' && !loc.selectedId && key === `new:${loc.newChatNonce}`) {
                    navigate(`/dashboard/assistant?conversation=${created.id}`, { replace: true });
                }
            }
            listChanged();
        } catch (err) {
            if (!aliveRef.current) return;
            const data = err?.response?.data || {};
            const failedPatch = {
                state: err?.response?.status === 409 && data.code === 'in_flight' ? 'processing' : 'failed',
                error: sendErrorMessage(err),
            };
            if (!conversationId && data.conversation_id) {
                // The thread (and the question) exist server-side already: attach
                // the failed send to it so a retry never creates a second thread.
                const targetId = Number(data.conversation_id);
                movePending(key, targetId, clientId, failedPatch);
                loadDetail(targetId, { silent: true });
                const loc = locationRef.current;
                if (loc.onAssistant && loc.view === 'chat' && !loc.selectedId && key === `new:${loc.newChatNonce}`) {
                    navigate(`/dashboard/assistant?conversation=${targetId}`, { replace: true });
                }
                listChanged();
            } else {
                patchPending(key, clientId, failedPatch);
            }
        }
    }, [appendMessages, listChanged, loadDetail, movePending, navigate, patchPending]);

    const send = useCallback((key, text) => {
        const content = text.trim();
        if (!content) return false;
        const busy = (pendingRef.current[key] || []).some((p) => p.state === 'sending');
        if (busy) return false;
        const item = { clientId: newClientId(), content, state: 'sending', error: null };
        setPending((prev) => ({ ...prev, [key]: [...(prev[key] || []), item] }));
        deliver(key, item);
        return true;
    }, [deliver]);

    // Retry never blindly re-posts: first reconcile with the server (the
    // earlier attempt may have been saved and answered after a timeout); the
    // re-post carries the same client_request_id, so the server replays
    // instead of storing the question twice.
    const retry = useCallback(async (key, clientId) => {
        const item = (pendingRef.current[key] || []).find((p) => p.clientId === clientId);
        if (!item || item.state === 'sending') return;
        if (typeof key === 'number') {
            patchPending(key, clientId, { state: 'sending', error: null });
            const fresh = await loadDetail(key, { silent: true });
            if (!aliveRef.current) return;
            if (fresh && hasReply(fresh, clientId)) {
                patchPending(key, clientId, null);
                listChanged();
                return;
            }
        }
        deliver(key, item);
    }, [deliver, listChanged, loadDetail, patchPending]);

    // A question saved server-side whose answer never arrived (e.g. the page
    // was reloaded after a failure): ask again under its own request id, so
    // the server answers that message instead of storing a new one.
    const resume = useCallback((key, clientId, content) => {
        if ((pendingRef.current[key] || []).some((p) => p.clientId === clientId)) return;
        const item = { clientId, content, state: 'sending', error: null };
        setPending((prev) => ({ ...prev, [key]: [...(prev[key] || []), item] }));
        deliver(key, item);
    }, [deliver]);

    const discard = useCallback((key, clientId) => patchPending(key, clientId, null), [patchPending]);

    // --- rename / delete (dialogs live once, here) -------------------------
    const [renameTarget, setRenameTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const restoreFocusRef = useRef(null);
    const requestRename = useCallback((conversation) => {
        restoreFocusRef.current = document.activeElement;
        setRenameTarget(conversation);
    }, []);
    const requestDelete = useCallback((conversation) => {
        restoreFocusRef.current = document.activeElement;
        setDeleteTarget(conversation);
    }, []);

    const rename = useCallback(async (id, title) => {
        const res = await api.patch(`${API.assistantConversations}${id}/`, { title });
        if (!aliveRef.current) return;
        const newTitle = res.data?.title ?? title;
        setDetails((prev) =>
            prev[id]?.data ? { ...prev, [id]: { ...prev[id], data: { ...prev[id].data, title: newTitle } } } : prev,
        );
        setRecent((prev) => ({
            ...prev,
            items: prev.items.map((c) => (c.id === id ? { ...c, title: newTitle } : c)),
        }));
        listChanged();
    }, [listChanged]);

    const remove = useCallback(async (id) => {
        await api.delete(`${API.assistantConversations}${id}/`);
        if (!aliveRef.current) return;
        setRecent((prev) => ({ ...prev, items: prev.items.filter((c) => c.id !== id) }));
        setDetails((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        setPending((prev) => {
            const next = { ...prev };
            delete next[id];
            return next;
        });
        if (locationRef.current.selectedId === id) {
            navigate('/dashboard/assistant', { replace: true });
        }
        listChanged();
    }, [listChanged, navigate]);

    const closeDialogs = useCallback(() => {
        setRenameTarget(null);
        setDeleteTarget(null);
        const el = restoreFocusRef.current;
        restoreFocusRef.current = null;
        if (el && document.contains(el)) setTimeout(() => el.focus?.(), 0);
    }, []);

    const value = useMemo(() => ({
        enabled,
        status,
        loadStatus,
        recent: recent.items.slice(0, RECENT_LIMIT),
        recentLoading: recent.loading,
        recentError: recent.error,
        refreshRecent,
        listVersion,
        selectedId,
        invalidParam,
        view,
        newChatNonce,
        openConversation,
        openNew,
        openHistory,
        details,
        loadDetail,
        drafts,
        setDraft,
        pending,
        send,
        retry,
        resume,
        discard,
        requestRename,
        requestDelete,
    }), [
        enabled, status, loadStatus, recent, refreshRecent, listVersion, selectedId, invalidParam, view,
        newChatNonce, openConversation, openNew, openHistory, details, loadDetail, drafts, setDraft,
        pending, send, retry, resume, discard, requestRename, requestDelete,
    ]);

    return (
        <AssistantContext.Provider value={value}>
            {children}
            {enabled && (
                <AssistantConversationDialogs
                    renameTarget={renameTarget}
                    deleteTarget={deleteTarget}
                    onRename={rename}
                    onDelete={remove}
                    onClose={closeDialogs}
                />
            )}
        </AssistantContext.Provider>
    );
}
